import { HttpParams } from '@angular/common/http'
import * as moment from 'moment'
import { TimeRepresentation } from '../../app/common/components/hierarch-menu/timerep.model'
import { Utils } from '../../app/common/utils/utils'
import { IDateable } from '../../app/ui/components/date-selector/idateable.interface'
import { PaginatedList, PaginatedSearchCriteria } from './base.model'
import { Month } from './months.enum'

export enum TimeCardStatus {
    // EDITED = Timecard may no longer be  overwritten with worklog.
    EDITED = 1,

    // DELETED = Timecard was once imported or created as new, but is now obsolete and no longer used.
    DELETED = 2,

    // LOCKED = Timecard is linked to a Timesheet, and can nog longer be edited, deleted or overwritten.
    LOCKED = 3,

    // NEW = A new timecard, without a reference to a worklog. It is created directly in the Timecard table.
    NEW = 4
}

/**
 * Most values are always defined because we get them from the backend. We're not creating instances from the Timecard class.
 * That's why some values are not-undefinable even though they're not seen in the constructor.
 */
export class TimeCard extends TimeRepresentation {
    date!: Date
    userName!: string
    fullName!: string
    employeeId!: number
    projectKey!: string
    timesheetProjectName!: string
    timesheetProjectId!: number
    clientName!: string
    tag!: string
    tags!: Set<string>
    tagMetaGroup!: string
    issueKey!: string
    issueTitle!: string
    worklogComment!: string
    issuePriority!: string
    timecardStatusEnum?: TimeCardStatus

    static fromJSON(json: TimeCardDTO): TimeCard {
        const date = new Date(json.date)
        // Processing #-tags is only supported after April 20, 2024.
        const processHashtagTags = date > new Date(2023, 3, 20)
        const tags = Utils.getAllTagsFromText(json.worklogComment, processHashtagTags)
        const result: TimeCard = Object.create(TimeCard.prototype)

        Object.assign(result, json, {
            date: new Date(json.date),
            timecardStatusEnum: json.timecardStatusEnum ? TimeCardStatus[json.timecardStatusEnum] : null,
            tags,
            tag: tags.values().next().value, // use the 1st tag from the multi-tag approach by default
            tagMetaGroup: `${employeeFullName(json)} - ${json.clientName} - ${json.timesheetProjectName}`
        })

        return result
    }
    get employeeFullName(): string {
        return this.fullName ? this.fullName : this.userName
    }
}

export interface TimeCardDTO extends Omit<TimeCard, 'date' | 'tags' | 'timecardStatusEnum'> {
    date: Date
    tags: Set<string>
    timecardStatusEnum?: TimeCardStatus
}

const employeeFullName = (timecard: TimeCard | TimeCardDTO): string =>
    timecard.fullName ? timecard.fullName : timecard.userName

export class TimeCardSearchCriteria extends PaginatedSearchCriteria implements IDateable {
    month?: Month
    year?: number
    employeeId?: number
    issueKey?: string
    startDate?: moment.Moment
    endDate?: moment.Moment
    excludedStatuses?: Array<TimeCardStatus>
    includeArchived?: boolean

    httpParams(): HttpParams {
        let params = super.httpParams()

        if (this.month !== undefined) {
            params = params.set('month', `${this.month}`)
        }
        if (this.year) {
            params = params.set('year', `${this.year}`)
        }
        if (this.employeeId) {
            params = params.set('employeeId', `${this.employeeId}`)
        }
        if (this.issueKey) {
            params = params.set('issueKey', this.issueKey)
        }
        if (this.startDate) {
            params = params.set('startDate', this.startDate.clone().startOf('day').utc(true).toISOString())
        }
        if (this.endDate) {
            params = params.set('endDate', this.endDate.clone().startOf('day').utc(true).toISOString())
        }
        if (this.excludedStatuses) {
            params = params.set('excludedStatuses', `${this.excludedStatuses}`)
        }
        if (this.includeArchived) {
            params = params.set('includeArchived', `${this.includeArchived}`)
        }

        return params
    }
}

export class TimeCardPaginatedList extends PaginatedList<TimeCard> {
    static fromJSON(json: any): TimeCardPaginatedList {
        const result: TimeCardPaginatedList = Object.create(TimeCardPaginatedList.prototype)

        return {
            ...result,
            ...json,
            items: json.items ? json.items.map((item: any) => TimeCard.fromJSON(item)) : null
        }
    }
}
